import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpResponse,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, tap, throwError } from 'rxjs';

import { TOAST_DISPLAY_DURATION } from './consts';

interface ErrorItem {
  Message?: string;
  message?: string;
}

type ResponseBody = Record<string, unknown>;

const getErrorMessage = (error: ErrorItem | null | undefined): string =>
  error?.Message || error?.message || '';

const hasErrors = (value: unknown): value is ErrorItem[] =>
  Array.isArray(value) && value.length > 0;

const getResponseErrors = (
  body: ResponseBody | null,
): ErrorItem[] | undefined =>
  (body?.['errors'] || body?.['Errors']) as ErrorItem[] | undefined;

const isFailedResponse = (body: ResponseBody | null): boolean =>
  (body?.['Success'] === false || body?.['success'] === false) &&
  (hasErrors(body?.['errors']) || hasErrors(body?.['Errors']));

const getHttpErrorDetails = (error: HttpErrorResponse): ErrorItem | null => {
  const body = error?.error as ResponseBody | null;
  const errors = body?.['errors'];
  if (hasErrors(errors)) {
    return errors[0];
  }
  const pascalErrors = body?.['Errors'];
  if (hasErrors(pascalErrors)) {
    return pascalErrors[0];
  }
  return null;
};

export const httpErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const snackBar = inject(MatSnackBar);

  const showError = (message: string): void => {
    if (message) {
      snackBar.open(message, 'Close', { duration: TOAST_DISPLAY_DURATION });
    }
  };

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        const body = event.body as ResponseBody | null;
        if (isFailedResponse(body)) {
          const errors = getResponseErrors(body);
          const message = getErrorMessage(errors?.[0]);
          showError(message);
          throw new Error(message || 'Unknown error occurred');
        }
      }
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        // 401 is handled by authInterceptor
        if (error.status === +HttpStatusCode.Unauthorized) {
          return throwError(() => error);
        }

        const errorDetails = getHttpErrorDetails(error);
        const message = getErrorMessage(errorDetails) || error.message;
        showError(message);
        return throwError(() => error);
      }
      return throwError(() => error);
    }),
  );
};
